/**
 * PartnersSection - Displays partner cards with category filters
 *
 * Shows all partners with category tags on each card.
 * Filter buttons are single-select: one category (or "All") is active at a time,
 * matching the FilterBar pill UI. Clicking a card's category tag activates that filter.
 * Partners are sourced from content/partners/*.yaml files.
 */

import PropTypes from "prop-types"
import { useState, useMemo } from "react"
import Link from "components/Link"
import FilterBar from "components/FilterBar"
import {
  useAllPartners,
  useActivePartnerCategories,
  PARTNER_CATEGORIES,
  getCategoryLabels,
} from "effects/usePartnersContent"
import * as css from "./PartnersSection.module.css"
import Container from "components/ui/Container"

function CategoryTag({ category, onClick, clickable = false }) {
  const Tag = clickable ? "button" : "span"
  return (
    <Tag
      type={clickable ? "button" : undefined}
      className={css.categoryTag}
      onClick={clickable ? onClick : undefined}
    >
      {category.label}
    </Tag>
  )
}

CategoryTag.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
}

function PartnerCard({
  name,
  logo,
  website,
  partnerPage,
  description,
  categories,
  onTagClick,
}) {
  const categoryLabels = getCategoryLabels(categories)
  // Link to internal partner page if available, otherwise external website
  const linkUrl = partnerPage || website
  const isExternal = !partnerPage

  return (
    <div className={css.partnerCard}>
      <Link
        href={linkUrl}
        openNewTab={isExternal}
        className={css.partnerLink}
        title={partnerPage ? `Learn more about ${name}` : `Visit ${name}`}
      >
        <div className={css.logoWrapper}>
          <img src={logo} alt={name} className={css.logo} />
        </div>
        <div className={css.partnerInfo}>
          <h4 className={css.partnerName}>{name}</h4>
          {description && <p className={css.partnerDesc}>{description}</p>}
        </div>
      </Link>
      {categoryLabels.length > 0 && (
        <div className={css.categoryTags}>
          {categoryLabels.map((cat) => (
            <CategoryTag
              key={cat.id}
              category={cat}
              onClick={() => onTagClick(cat.id)}
              clickable
            />
          ))}
        </div>
      )}
    </div>
  )
}

PartnerCard.propTypes = {
  name: PropTypes.string.isRequired,
  logo: PropTypes.string.isRequired,
  website: PropTypes.string.isRequired,
  partnerPage: PropTypes.string,
  description: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string),
  onTagClick: PropTypes.func.isRequired,
}

function PartnersSection() {
  const allPartners = useAllPartners()
  const activeCategories = useActivePartnerCategories()
  // Single-select to match the FilterBar UI (one pill highlighted at a time).
  // null = "All". Re-clicking the active category returns to "All".
  const [activeFilter, setActiveFilter] = useState(null)

  // Select a category (or toggle back to "All" when re-clicking the active one).
  const selectFilter = (categoryId) => {
    setActiveFilter((prev) => (prev === categoryId ? null : categoryId))
  }

  // Reset to "All"
  const clearFilters = () => {
    setActiveFilter(null)
  }

  // Filter partners by the active category (null = show all)
  const filteredPartners = useMemo(() => {
    if (!activeFilter) {
      return allPartners
    }
    return allPartners.filter((partner) =>
      (partner.categories || []).includes(activeFilter)
    )
  }, [allPartners, activeFilter])

  // If no partners yet, show coming soon message
  if (allPartners.length === 0) {
    return (
      <Container section>
        <div className="text-center py-12">
          <p className="text-gray-500">Partner directory coming soon.</p>
        </div>
      </Container>
    )
  }

  // Build filter list: "All" + active categories only (sorted by order)
  const partnerFilters = [
    { id: null, label: "All" },
    ...Object.values(PARTNER_CATEGORIES)
      .filter((cat) => activeCategories.some((c) => c.id === cat.id))
      .sort((a, b) => a.order - b.order),
  ]

  return (
    <section className={css.partnersSection}>
      <Container section>
        <FilterBar
          filters={partnerFilters}
          activeFilter={activeFilter}
          onChange={selectFilter}
        />

        {/* Partner Grid */}
        <div className={css.partnerGrid}>
          {filteredPartners.map((partner) => (
            <PartnerCard
              key={partner.slug}
              name={partner.name}
              logo={partner.logo}
              website={partner.website}
              partnerPage={partner.partnerPage}
              description={partner.description}
              categories={partner.categories}
              onTagClick={setActiveFilter}
            />
          ))}
        </div>

        {/* No results message */}
        {filteredPartners.length === 0 && activeFilter && (
          <div className={css.noResults}>
            <p>No partners found for the selected filters.</p>
            <button
              type="button"
              className={css.clearFiltersButton}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        )}
      </Container>
    </section>
  )
}

export default PartnersSection
